import type { ChangeEventHandler, ComponentType, FC } from 'react'
import { useCallback, useMemo, useRef, useState } from 'react'

import type { OnlyFansList } from '@api'
import { useUnit } from 'effector-react'
import debounce from 'lodash/debounce'
import type { ListOnScrollProps } from 'react-window'
import { type ListChildComponentProps, VariableSizeList } from 'react-window'

import { IconLoading } from '@/assets/icons/icon-loading.tsx'
import SearchIcon from '@/assets/icons/icon-search.svg'
import { Button } from '@/components/ui/button.tsx'
import { Checkbox } from '@/components/ui/checkbox.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { TextField } from '@/components/ui/text-field.tsx'
import { useToast } from '@/components/ui/toast.tsx'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import type { DialogProps } from '@/dialogs/types'
import { $activeChatDetails } from '@/models/chattingUI/chats/chats.ts'
import { openDialog } from '@/models/dialogs.ts'
import { useLists } from '@/modules/chatting/hooks/use-lists.ts'
import { useManageUserLists } from '@/modules/chatting/hooks/use-manage-user-lists.ts'
import { unescapeHtmlText } from '@/modules/chatting/ui/chat-window/fan-info/utils.ts'
import { FilterSortOrderOptions } from '@/modules/chatting/ui/components/filter-sort-order-options.tsx'
import { useSizeObserver } from '@/modules/chatting/ui/sidebar/use-size-observer.ts'

import { ListMembersAvatars } from '../../modules/chatting/ui/chat-window/lists/members-avatars.tsx'

const getItemSize = () => 83

interface Props extends DialogProps {}

export const SaveToListDialog: FC<Props> = ({ isOpen, onClose }) => {
  const activeChat = useUnit($activeChatDetails)

  const {
    withUser: { id: userId, listsStates },
  } = activeChat
  const [selectedLists, setSelectedLists] = useState<string[]>(
    listsStates?.map(({ id }) => id) || [],
  )
  const toast = useToast()
  const [parentRef, width, height] = useSizeObserver()
  const lastVisibleIndexRef = useRef<number | null>(null)

  const {
    lists,
    isLoading,
    canLoadMore,
    onLoadMore,
    isFetchingNextPage,
    sort,
    order,
    setOrder,
    setSort,
    sortOptions,
    orderOptions,
    setQuery,
    isSortAndOrderDisabled,
  } = useLists('all-lists-extended', {
    enabled: isOpen,
    related_user: userId,
  })
  const filteredLists = lists.filter((list) => list.canAddUsers)
  const { updateListsStatesMutation } = useManageUserLists()
  const handleSearchChange = debounce<ChangeEventHandler<HTMLInputElement>>(
    ({ target: { value } }) =>
      (!value.length || value.length > 1) && setQuery(value),
    500,
  )

  const isListAddedTo = useCallback(
    (listId: string) => selectedLists.indexOf(listId) >= 0,
    [selectedLists],
  )

  const handleToggleCheck = useCallback(
    (listId: OnlyFansList['id'], state: boolean) => {
      setSelectedLists((prev) => {
        return state ? prev.concat(listId) : prev.filter((id) => id !== listId)
      })
    },
    [],
  )

  const { add, remove } = useMemo(() => {
    const editableLists = filteredLists.filter(
      ({ id }) => !!listsStates?.find((list) => list.id === id),
    )

    const remove = editableLists.filter(({ id }) => !isListAddedTo(id))
    const add = selectedLists
      .filter(
        (id) =>
          filteredLists.find((list) => list.id === id) &&
          !editableLists.find((list) => list.id === id),
      )
      .reduce<OnlyFansList[]>((acc, id) => {
        const list = filteredLists.find((list) => list.id === id)

        if (list) {
          acc.push(list)
        }

        return acc
      }, [])

    return { add, remove }
  }, [filteredLists, listsStates, selectedLists, isListAddedTo])

  const hasChanges = add.length > 0 || remove.length > 0

  const handleSave = () => {
    if (!hasChanges) {
      toast.error('No changes detected')

      return
    }

    updateListsStatesMutation
      .mutateAsync({ remove, add, userId: activeChat.withUser.id })
      .then(onClose)
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleScroll = useCallback(
    debounce(({ scrollDirection }: ListOnScrollProps) => {
      const lastItemIndex = lastVisibleIndexRef.current

      if (
        lastItemIndex === null ||
        lastItemIndex !== filteredLists.length - 1 ||
        !canLoadMore
      ) {
        return
      }

      if (scrollDirection === 'forward') {
        onLoadMore()
      }
    }, 100),
    [onLoadMore, canLoadMore, filteredLists.length],
  )

  const renderListItem: ComponentType<ListChildComponentProps<OnlyFansList[]>> =
    useCallback(
      ({ data, index, style }) => {
        const list = data[index]

        lastVisibleIndexRef.current = index

        return (
          <label
            htmlFor={list.id}
            style={style}
            className="hover:bg-primary-50 dark:hover:bg-secondary-600 flex w-full cursor-pointer flex-col"
          >
            <div
              className="group flex h-[82px] items-center gap-3 p-4"
              key={list.id}
            >
              <Checkbox
                id={list.id}
                onCheckedChange={(value) =>
                  handleToggleCheck(
                    list.id,
                    typeof value === 'boolean' ? value : false,
                  )
                }
                checked={isListAddedTo(list.id)}
              />
              <ListMembersAvatars
                size={50}
                members={list.users}
                avatarClassName="group-hover:border-primary-50 dark:group-hover:border-secondary-600"
              />
              <p className="font-medium">{unescapeHtmlText(list.name)}</p>
            </div>
            <div className="ml-[78px] mr-4 border-b" />
          </label>
        )
      },
      [handleToggleCheck, isListAddedTo],
    )

  const handleClickCustomizeOrder = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.COLLECTIONS,
      isPersistent: true,
      config: { openInCustomOrderEditMode: true },
    })
    onClose()
  }, [onClose])
  const handleClickCreateNew = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.CREATE_LIST,
      forUserId: userId,
    })
  }, [userId])
  const customizeAction = (
    <>
      <div className="w-full border-b" />
      <button
        className="mt-1 w-full rounded px-3 py-1.5 text-left text-sm font-medium hover:bg-secondary-50 dark:hover:bg-secondary-600"
        onClick={handleClickCustomizeOrder}
      >
        Customize Order
      </button>
    </>
  )

  return (
    <Dialog
      bigDialog
      show={isOpen}
      onClose={onClose}
      showCloseIcon={false}
      panelClassName="sm:p-8 lg:max-w-[700px]"
      childrenClassName="max-w-full overflow-hidden"
    >
      <div className="space-y-6">
        <Dialog.Header>
          <Dialog.Title>Save to List</Dialog.Title>
        </Dialog.Header>
        <div className="flex items-center gap-3">
          <TextField
            placeholder="Search"
            className="dark:border-secondary-400 flex-1 resize-none"
            prefix={<SearchIcon className="text-title" />}
            onChange={handleSearchChange}
          />
          <FilterSortOrderOptions
            order={order}
            setOrder={setOrder}
            sort={sort}
            setSort={setSort}
            sortOptions={sortOptions}
            orderOptions={orderOptions}
            extra={customizeAction}
            disabled={isSortAndOrderDisabled}
          />
        </div>
        <div
          className="no-scrollbar border-border-secondary bg-card relative !mt-3 max-h-[524px] overflow-hidden rounded-lg border py-4"
          ref={parentRef}
        >
          {isLoading && !filteredLists.length && (
            <IconLoading className="mx-auto min-h-[490px] animate-spin" />
          )}
          {!isLoading && !filteredLists.length && (
            <div className="flex min-h-[490px] flex-col items-center justify-center">
              <p className="text-secondary-400 text-center">No lists found</p>
            </div>
          )}
          {!isLoading && !!filteredLists.length && (
            <div className="relative min-h-[490px] w-full">
              <VariableSizeList
                onScroll={handleScroll}
                className="scrollbar-hide"
                itemData={filteredLists}
                itemSize={getItemSize}
                height={height}
                itemCount={filteredLists?.length ?? 0}
                width={width}
              >
                {renderListItem}
              </VariableSizeList>
            </div>
          )}
          {isFetchingNextPage && (
            <div className="bg-card sticky bottom-0 left-0 right-0 pb-1 pt-3">
              <IconLoading className="mx-auto animate-spin" />
            </div>
          )}
        </div>
        <Dialog.ClosableFooter
          className="flex"
          onClose={onClose}
          buttonClassName="flex-1 min-w-max"
        >
          <Button
            variant="outline"
            onClick={handleClickCreateNew}
            className="min-w-max flex-1"
          >
            New List
          </Button>
          <Button
            isLoading={updateListsStatesMutation.isLoading}
            onClick={handleSave}
            className="min-w-max flex-1"
            disabled={!hasChanges}
          >
            Save
          </Button>
        </Dialog.ClosableFooter>
      </div>
    </Dialog>
  )
}
